import { useEffect, useReducer, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Briefcase,
  ChevronDown,
  CreditCard,
  Database,
  Grid,
  Icon,
  MapPin,
  Package,
  Shield,
  Shuffle,
  TrendingUp,
  Truck,
  Users,
} from "react-feather";

import { AppState } from "../../redux/types";
import { MenuItems, menuOptionsInitialState, menuReducer } from "../../utils/navbarReducerFunction";
import { checkUserPermissions } from "../../utils/checkUserPermissions";
import { cn } from "../../utils/commonFunctions";
import { masterMenuItems } from "../config/masterMenuConfig";
import { firstMileMenuItems } from "../config/firstMileMenuConfig";
import { middleMileMenuItems } from "../config/middleMileMenuConfig";
import { lastMileMenuItems } from "../config/lastMileMenuConfig";
import { ftlMenuItems } from "../config/ftlMenuConfig";
import { accountMenuItems } from "../config/accountMenuConfig";

const SIDEBAR_COLLAPSED_WIDTH = 72;
const SIDEBAR_EXPANDED_WIDTH = 260;

type NavChild = { label: string; path: string; permission?: string };

type NavEntry =
  | { kind: "link"; label: string; path: string; icon: Icon }
  | { kind: "group"; label: string; icon: Icon; action: MenuItems; children: NavChild[]; permission?: string };

const salesMenuItems: NavChild[] = [
  { label: "Leads", path: "/sales/leads" },
  { label: "My Leads", path: "/sales/my-leads" },
  { label: "Activity", path: "/sales/activities" },
  { label: "My Activities", path: "/sales/my-activities" },
  { label: "Reports", path: "/sales/reports" },
  { label: "Expense", path: "/sales/expenses" },
  { label: "My Expense", path: "/sales/my-expenses" },
  { label: "Create Sales Expense", path: "/sales/add-sales-expense" },
  { label: "Sales Funnel", path: "/sales/sales-Funnel" },
];

const hrMenuItems: NavChild[] = [
  { label: "Attendance Management", path: "/hr/attendance-management" },
  { label: "My Attendance", path: "/hr/my-attendance" },
  { label: "Attendance Report", path: "/hr/attendance-report" },
  { label: "Role", path: "/master/roles" },
  { label: "Department", path: "/master/departments" },
  { label: "User", path: "/master/users" },
  { label: "Payroll Management", path: "/hr/payroll", permission: "HR_MANAGE_PAYROLL_TABLE" },
  { label: "Leave Management", path: "/hr/leave-management" },
  { label: "My Leaves", path: "/hr/my-leaves" },
];

const permissionMenuItems: NavChild[] = [
  { label: "Permission", path: "/permissions/permission" },
  { label: "Permissions In Role", path: "/permissions/role-permission" },
  { label: "Sections", path: "/permissions/section" },
  { label: "Department In Section", path: "/permissions/department-section" },
];

const navSections: Array<{ title: string; entries: NavEntry[] }> = [
  {
    title: "Overview",
    entries: [
      { kind: "link", label: "Dashboard", path: "/dashboard", icon: Grid },
      { kind: "link", label: "My Space", path: "/my-space", icon: Briefcase },
    ],
  },
  {
    title: "Operations",
    entries: [
      { kind: "group", label: "Master", icon: Database, action: MenuItems.MASTER, children: masterMenuItems },
      { kind: "group", label: "First Mile", icon: Package, action: MenuItems.FIRST_MILE, children: firstMileMenuItems },
      { kind: "group", label: "Middle Mile", icon: Shuffle, action: MenuItems.MIDDLE_MILE, children: middleMileMenuItems },
      { kind: "group", label: "Last Mile", icon: MapPin, action: MenuItems.LAST_MILE, children: lastMileMenuItems },
      { kind: "group", label: "Fleet", icon: Truck, action: MenuItems.FTL, children: ftlMenuItems },
    ],
  },
  {
    title: "Business",
    entries: [
      { kind: "group", label: "Sales", icon: TrendingUp, action: MenuItems.SALES, children: salesMenuItems },
      { kind: "group", label: "Accounts", icon: CreditCard, action: MenuItems.ACCOUNTS, children: accountMenuItems },
      { kind: "group", label: "HR", icon: Users, action: MenuItems.HR, children: hrMenuItems },
    ],
  },
  {
    title: "Admin",
    entries: [
      {
        kind: "group",
        label: "Permissions",
        icon: Shield,
        action: MenuItems.PERMISSIONS,
        children: permissionMenuItems,
        permission: "PERMISSION_LIST",
      },
    ],
  },
];

// The current route belongs to the longest menu path it starts with, so "/a" and "/a/b" are not both active
const matchesPath = (pathname: string, path: string) => pathname === path || pathname.startsWith(`${path}/`);

const Sidebar = () => {
  const { pathname } = useLocation();
  const userPermissions = useSelector((state: AppState) => state?.user?.loggedInUserData?.permissions);
  const [isExpanded, setIsExpanded] = useState(false);
  const [menuState, setMenuItem] = useReducer(menuReducer, menuOptionsInitialState);

  useEffect(() => {
    const sidebarWidth = isExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;
    document.documentElement.style.setProperty("--app-sidebar-width", `${sidebarWidth}px`);
  }, [isExpanded]);

  const isGroupOpen = (action: MenuItems) => {
    switch (action) {
      case MenuItems.MASTER:
        return !!menuState.master;
      case MenuItems.FIRST_MILE:
        return !!menuState.operationSubMenu?.firstMile;
      case MenuItems.MIDDLE_MILE:
        return !!menuState.operationSubMenu?.middleMile;
      case MenuItems.LAST_MILE:
        return !!menuState.operationSubMenu?.lastMile;
      case MenuItems.FTL:
        return !!menuState.ftl;
      case MenuItems.SALES:
        return !!menuState.sales;
      case MenuItems.ACCOUNTS:
        return !!menuState.account;
      case MenuItems.HR:
        return !!menuState.hr;
      case MenuItems.PERMISSIONS:
        return !!menuState.permission;
      default:
        return false;
    }
  };

  const isAllowed = (permission?: string) => !permission || checkUserPermissions(userPermissions, permission);

  const visibleSections = navSections
    .map((section) => ({
      ...section,
      entries: section.entries
        .filter((entry) => entry.kind === "link" || isAllowed(entry.permission))
        .map((entry) =>
          entry.kind === "group" ? { ...entry, children: entry.children.filter((child) => isAllowed(child.permission)) } : entry
        ),
    }))
    .filter((section) => section.entries.length > 0);

  const activePath = visibleSections
    .flatMap((section) => section.entries)
    .flatMap((entry) => (entry.kind === "link" ? [entry.path] : entry.children.map((child) => child.path)))
    .filter((path) => matchesPath(pathname, path))
    .sort((a, b) => b.length - a.length)[0];

  return (
    <aside
      className="fixed left-0 top-[68px] z-[998] h-[calc(100vh-68px)]"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <nav
        className={cn(
          "flex h-full flex-col border-r border-slate-200 bg-white transition-[width,box-shadow] duration-300",
          isExpanded ? "w-[260px] shadow-[8px_0_32px_-12px_rgba(15,23,42,0.18)]" : "w-[72px]"
        )}
      >
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 [scrollbar-width:thin]">
          {visibleSections.map((section, sectionIndex) => (
            <div key={section.title} className={cn(sectionIndex > 0 && "mt-4")}>
              {/* section title, or a short rule while collapsed */}
              <div className="mb-1.5 flex h-4 items-center px-3">
                {isExpanded ? (
                  <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                    {section.title}
                  </span>
                ) : (
                  <span className="mx-auto h-px w-5 bg-slate-200" />
                )}
              </div>

              <ul className="flex flex-col gap-0.5">
                {section.entries.map((entry) => {
                  const EntryIcon = entry.icon;

                  if (entry.kind === "link") {
                    const isActive = activePath === entry.path;
                    return (
                      <li key={entry.path}>
                        <Link
                          to={entry.path}
                          title={isExpanded ? undefined : entry.label}
                          className={cn(
                            "relative flex h-10 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors",
                            isActive
                              ? "bg-[#E1383B] text-white shadow-md shadow-[#E1383B]/25"
                              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                          )}
                        >
                          <EntryIcon size={18} className="shrink-0" />
                          <span className={cn("truncate transition-opacity duration-200", isExpanded ? "opacity-100" : "opacity-0")}>
                            {entry.label}
                          </span>
                        </Link>
                      </li>
                    );
                  }

                  const isOpen = isGroupOpen(entry.action);
                  const hasActiveChild = entry.children.some((child) => child.path === activePath);

                  return (
                    <li key={entry.label}>
                      <button
                        type="button"
                        onClick={() => setMenuItem({ type: entry.action })}
                        title={isExpanded ? undefined : entry.label}
                        aria-expanded={isOpen && isExpanded}
                        className={cn(
                          "relative flex h-10 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-medium transition-colors",
                          hasActiveChild
                            ? "bg-[#E1383B]/10 text-[#E1383B]"
                            : isOpen && isExpanded
                              ? "bg-slate-100 text-slate-900"
                              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        )}
                      >
                        {hasActiveChild && (
                          <span className="absolute -left-3 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-[#E1383B]" />
                        )}
                        <EntryIcon size={18} className="shrink-0" />
                        <span
                          className={cn(
                            "flex-1 truncate transition-opacity duration-200",
                            isExpanded ? "opacity-100" : "opacity-0"
                          )}
                        >
                          {entry.label}
                        </span>
                        <ChevronDown
                          size={16}
                          className={cn(
                            "shrink-0 text-slate-400 transition-all duration-200",
                            isExpanded ? "opacity-100" : "opacity-0",
                            isOpen && "rotate-180"
                          )}
                        />
                      </button>

                      {isOpen && isExpanded && (
                        <ul className="relative ml-[1.35rem] mt-1 animate-slide-down border-l border-slate-200 py-0.5 pl-3">
                          {entry.children.map((child) => {
                            const isChildActive = child.path === activePath;
                            return (
                              <li key={`${child.path}-${child.label}`}>
                                <Link
                                  to={child.path}
                                  className={cn(
                                    "relative flex items-center rounded-lg px-2.5 py-1.5 text-[13px] transition-colors",
                                    isChildActive
                                      ? "bg-[#E1383B]/10 font-semibold text-[#E1383B]"
                                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                                  )}
                                >
                                  {isChildActive && (
                                    <span className="absolute -left-[17px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full border-2 border-white bg-[#E1383B]" />
                                  )}
                                  <span className="truncate">{child.label}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* logo road stripe */}
        <div
          aria-hidden="true"
          className="h-[3px] shrink-0 bg-[linear-gradient(90deg,#E1383B_0%,#E1383B_70%,#EF7F1A_70%,#EF7F1A_80%,#ffffff_80%,#ffffff_90%,#009846_90%,#009846_100%)]"
        />
      </nav>
    </aside>
  );
};

export default Sidebar;
